import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type RollupDay } from '../lib/api';
import { Card } from '../components/Card';
import { PageSpinner } from '../components/Spinner';
import { cn } from '../lib/cn';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';
import {
  StatTile, Legend, LineChart, StackedBars, Heatmap, RankedBars,
  seriesHue, OTHER_HUE, type StackedDay,
} from '../components/Viz';
import { LineChart as LineChartIcon, RefreshCw, Table2, AlertTriangle } from 'lucide-react';

// ── Founder analytics — who came in when, and what they did ─────────────────
//
// Two independent sources joined on a shared date axis:
//   · ACQUISITION — GET /admin/observatory/acquisition. Arrivals per day,
//     derived from USER#{id}/CONSENT.consentedAt via GSI1. Consent is not
//     logged to the audit timeline, so this is the ONLY durable record of when
//     someone first showed up.
//   · ROLLUPS — GET /admin/observatory/rollups. The permanent daily audit
//     aggregates written by src/lambda/dailyRollup: events, distinct actors,
//     action mix, hour-of-day, top actors.
//
// Everything here is server-side aggregated. No raw event row and no user
// roster reaches the browser; the only identities on the page are the ten
// top actors a founder is entitled to see, and those are purge-covered — an
// erased user reads as "Former player".
//
// Charts are hand-rolled (components/Viz.tsx) per the house no-chart-library
// rule. Two measures of different scale never share a y-axis: arrivals and
// activity are separate plots on a common date axis, deliberately.

const WINDOWS = [7, 30, 90] as const;
const TOP_ACTION_SLOTS = 6;
const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOUR_LABELS = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00 UTC`);
const PURGED = 'FORMER_PLAYER';

/** Short axis label — '2026-08-06' → '06 Aug'. */
function shortDay(day: string): string {
  const d = new Date(`${day}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return day;
  return `${String(d.getUTCDate()).padStart(2, '0')} ${d.toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' })}`;
}

/** Monday-first day-of-week index for a yyyy-mm-dd string. */
function dowIndex(day: string): number {
  const js = new Date(`${day}T00:00:00.000Z`).getUTCDay(); // 0 = Sunday
  return (js + 6) % 7;
}

export function UserAnalytics() {
  const [days, setDays] = useState<number>(30);
  const [showTable, setShowTable] = useState(false);

  const acquisition = useQuery({
    queryKey: ['admin', 'observatory', 'acquisition', days],
    queryFn: () => api.admin.observatoryAcquisition(days),
  });
  const rollups = useQuery({
    queryKey: ['admin', 'observatory', 'rollups', days],
    queryFn: () => api.admin.observatoryRollups(days),
  });

  const isLoading = acquisition.isLoading || rollups.isLoading;
  const isFetching = acquisition.isFetching || rollups.isFetching;
  const refetch = () => { void acquisition.refetch(); void rollups.refetch(); };

  const view = useMemo(() => {
    const axis = acquisition.data?.days ?? [];
    const rollupRows: RollupDay[] = rollups.data ?? [];
    const byDay = new Map(rollupRows.map(r => [r.day, r]));

    // ── Series on the shared, dense date axis ───────────────────────────────
    const arrivals = axis.map(d => ({ label: shortDay(d.day), value: d.arrivals }));
    const actives = axis.map(d => ({
      label: shortDay(d.day),
      value: byDay.get(d.day)?.uniqueActors?.count ?? 0,
    }));

    // ── Action mix: top N by total volume, tail folded into "Other" ─────────
    // A 7th action is never a generated hue — the fixed ramp has six slots and
    // everything past them collapses into one neutral bucket.
    const actionTotals = new Map<string, number>();
    for (const row of rollupRows) {
      for (const [action, n] of Object.entries(row.byAction ?? {})) {
        actionTotals.set(action, (actionTotals.get(action) ?? 0) + n);
      }
    }
    const rankedActions = [...actionTotals.entries()].sort((a, b) => b[1] - a[1]);
    const topActions = rankedActions.slice(0, TOP_ACTION_SLOTS).map(([a]) => a);
    const hasOther = rankedActions.length > TOP_ACTION_SLOTS;

    // Colour follows the ENTITY, never its rank.
    //
    // Slots are handed out in alphabetical order of the action NAME, not in
    // volume order. Assigning by rank looks fine until you switch the window:
    // two actions swap places on the leaderboard and the whole chart repaints,
    // so the eye reads "something changed colour" as "something changed", and
    // you cannot follow one action across two views. Keying on the name means
    // a re-rank is invisible — only a genuinely different SET of actions can
    // shift anything, and then only the members that actually changed.
    const hueOf = new Map(
      [...topActions].sort((a, b) => a.localeCompare(b)).map((a, i) => [a, seriesHue(i)]),
    );

    const stacked: StackedDay[] = axis.map(d => {
      const row = byDay.get(d.day);
      const segments = topActions.map(action => ({
        key: action,
        value: row?.byAction?.[action] ?? 0,
        color: hueOf.get(action)!,
      }));
      if (hasOther) {
        const known = segments.reduce((s, seg) => s + seg.value, 0);
        segments.push({ key: 'Other', value: Math.max(0, (row?.totalEvents ?? 0) - known), color: OTHER_HUE });
      }
      return { label: shortDay(d.day), segments };
    });

    const legendItems = [
      ...topActions.map(a => ({ label: a, color: hueOf.get(a)! })),
      ...(hasOther ? [{ label: 'Other', color: OTHER_HUE }] : []),
    ];

    // ── Rhythm: day-of-week × hour, summed across the window ────────────────
    // Aggregated rather than one row per calendar day: at 90 days a per-day
    // grid is 90 rows nobody reads, and the actual question ("when are people
    // online?") is answered better by the weekly shape.
    const grid = DOW.map(() => new Array(24).fill(0) as number[]);
    for (const row of rollupRows) {
      const r = dowIndex(row.day);
      if (r < 0 || r > 6) continue;
      (row.byHour ?? []).forEach((n, h) => { if (h < 24) grid[r][h] += n; });
    }
    const heatRows = DOW.map((label, i) => ({ label, values: grid[i] }));

    // ── Ranked people and channels ──────────────────────────────────────────
    const actorTotals = new Map<string, number>();
    for (const row of rollupRows) {
      for (const a of row.topActors ?? []) {
        actorTotals.set(a.actorId, (actorTotals.get(a.actorId) ?? 0) + a.count);
      }
    }
    const topActors = [...actorTotals.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([actorId, value]) => ({
        label: actorId === PURGED ? 'Former player (erased)' : actorId,
        value,
        muted: actorId === PURGED,
      }));

    const sourceTotals = new Map<string, number>();
    for (const row of rollupRows) {
      for (const [src, n] of Object.entries(row.bySource ?? {})) {
        sourceTotals.set(src, (sourceTotals.get(src) ?? 0) + n);
      }
    }
    const topSources = [...sourceTotals.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([label, value]) => ({ label, value }));

    const totalEvents = rollupRows.reduce((s, r) => s + (r.totalEvents ?? 0), 0);
    const totalErrors = rollupRows.reduce((s, r) => s + (r.errorCount ?? 0), 0);
    const peakActives = Math.max(0, ...actives.map(a => a.value));
    const anyRollupTruncated = rollupRows.some(r => r.truncated);

    return {
      axis, arrivals, actives, stacked, legendItems, heatRows,
      topActors, topSources, totalEvents, totalErrors, peakActives,
      rollupDayCount: rollupRows.length, anyRollupTruncated,
    };
  }, [acquisition.data, rollups.data]);

  const acq = acquisition.data;

  return (
    <div>
      <div className={cn('mb-8 flex items-center gap-3', SETTLE)} style={settleDelay(0)}>
        <LineChartIcon className="h-7 w-7 text-accent-500" />
        <div>
          <h1 className="text-2xl font-bold text-white">User analytics</h1>
          <p className="text-xs text-gray-500">
            Who arrived when, and what they did. Server-side aggregates only — no raw event rows
            and no user roster leave the API.
          </p>
        </div>
      </div>

      {/* Filters in one row above the charts, never scattered between them. */}
      <div className={SETTLE} style={settleDelay(1)}>
        <Card className="mb-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Window</span>
            {WINDOWS.map(w => (
              <button
                key={w}
                onClick={() => setDays(w)}
                className={cn(
                  'rounded-lg px-2.5 py-1 text-xs font-semibold',
                  MORPH,
                  days === w ? 'bg-accent-500/15 text-accent-500' : 'text-gray-500 hover:text-gray-300',
                )}
              >
                {w} days
              </button>
            ))}
            <button
              onClick={() => setShowTable(v => !v)}
              className={cn('ml-auto btn-ghost flex items-center gap-1.5 text-xs', MORPH)}
              title="Toggle the underlying numbers as a table"
            >
              <Table2 className="h-3.5 w-3.5" />
              {showTable ? 'Hide table' : 'Show table'}
            </button>
            <button onClick={refetch} className={cn('btn-ghost px-2', MORPH)} title="Refresh">
              <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
            </button>
          </div>
        </Card>
      </div>

      {isLoading ? (
        <PageSpinner />
      ) : (
        <div className="space-y-4">
          {/* Truncation is never silent — an undercounted chart that looks
              complete is worse than no chart. */}
          {(acq?.truncated || view.anyRollupTruncated) && (
            <Card className="border-amber-500/30">
              <p className="flex items-start gap-2 text-xs text-amber-300/90">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  These figures are an <strong>undercount</strong> — a query page cap was hit while
                  reading the source rows. Treat the shapes as directional, not the totals.
                </span>
              </p>
            </Card>
          )}

          <div className={cn('grid grid-cols-2 gap-3 sm:grid-cols-5', SETTLE)} style={settleDelay(2)}>
            <StatTile
              label="Total users"
              value={acq?.totalUsers ?? 0}
              hint={acq?.firstArrival ? `since ${shortDay(acq.firstArrival)}` : undefined}
            />
            <StatTile label={`New (${days}d)`} value={acq?.inWindow ?? 0} />
            <StatTile label="Peak daily actives" value={view.peakActives} />
            <StatTile label={`Events (${days}d)`} value={view.totalEvents} />
            <StatTile
              label="Errors"
              value={view.totalErrors}
              hint={view.totalEvents > 0 ? `${((view.totalErrors / view.totalEvents) * 100).toFixed(1)}% of events` : undefined}
            />
          </div>

          {/* Arrivals and activity are two scales, so two plots on one shared
              date axis — never a dual-axis chart. */}
          <div className={SETTLE} style={settleDelay(3)}>
            <Card>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500">
                New users per day
              </p>
              <p className="mb-3 text-[11px] text-gray-600">
                When someone first accepted the privacy notice — the only durable record of arrival.
              </p>
              <LineChart points={view.arrivals} />
            </Card>
          </div>

          <div className={SETTLE} style={settleDelay(4)}>
            <Card>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Active users per day
              </p>
              <p className="mb-3 text-[11px] text-gray-600">
                Distinct people who did something that day, from the permanent audit rollups.
              </p>
              <LineChart points={view.actives} color="#3987e5" />
            </Card>
          </div>

          <div className={SETTLE} style={settleDelay(5)}>
            <Card>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500">
                What they did
              </p>
              <p className="mb-3 text-[11px] text-gray-600">
                Audited actions per day, by type. Anything outside the top {TOP_ACTION_SLOTS} is folded into “Other”.
              </p>
              <StackedBars days={view.stacked} />
              {view.legendItems.length > 0 && <Legend items={view.legendItems} />}
            </Card>
          </div>

          <div className={SETTLE} style={settleDelay(6)}>
            <Card>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500">
                When they show up
              </p>
              <p className="mb-3 text-[11px] text-gray-600">
                Activity by weekday and hour, summed across the window. Brighter means busier;
                an unlit cell means nothing happened at all.
              </p>
              <Heatmap rows={view.heatRows} colLabels={HOUR_LABELS} rowLabel={r => r.label} />
            </Card>
          </div>

          <div className={cn('grid grid-cols-1 gap-4 md:grid-cols-2', SETTLE)} style={settleDelay(7)}>
            <Card>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Most active users
              </p>
              <p className="mb-3 text-[11px] text-gray-600">
                Top ten by audited actions. Erased users show as “Former player”.
              </p>
              <RankedBars items={view.topActors} />
            </Card>
            <Card>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Where activity comes from
              </p>
              <p className="mb-3 text-[11px] text-gray-600">
                Discord bot versus web portal versus automated paths.
              </p>
              <RankedBars items={view.topSources} color="#199e70" />
            </Card>
          </div>

          {/* A table view always exists — identity and magnitude must be
              readable without relying on colour or a mouse. */}
          {showTable && (
            <Card>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                The numbers
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wide text-gray-600">
                      <th className="pb-2 font-semibold">Day</th>
                      <th className="pb-2 text-right font-semibold">New users</th>
                      <th className="pb-2 text-right font-semibold">Active users</th>
                      <th className="pb-2 text-right font-semibold">Events</th>
                      <th className="pb-2 text-right font-semibold">Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view.axis.map((d, i) => {
                      const row = (rollups.data ?? []).find(r => r.day === d.day);
                      return (
                        <tr key={d.day} className="border-t border-white/[0.04]">
                          <td className="py-1.5 font-mono text-gray-400">{d.day}</td>
                          <td className="py-1.5 text-right font-mono text-gray-300">{d.arrivals}</td>
                          <td className="py-1.5 text-right font-mono text-gray-300">{view.actives[i]?.value ?? 0}</td>
                          <td className="py-1.5 text-right font-mono text-gray-300">{row?.totalEvents ?? 0}</td>
                          <td className="py-1.5 text-right font-mono text-gray-500">{row?.errorCount ?? 0}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {view.rollupDayCount === 0 && (
            <Card>
              <p className="text-xs text-gray-500">
                No daily audit rollups exist for this window yet. The rollup Lambda writes one row
                per day at 04:30 UTC, so activity charts stay empty until it has run — the arrivals
                chart above reads from consent records directly and does not wait for it.
              </p>
            </Card>
          )}

          <p className="px-1 text-[10px] leading-relaxed text-gray-600">
            Privacy: arrivals are counted from POPIA consent records and are never returned with an
            identity attached. Daily rollups keep counts, not rosters. The only named people on this
            page are the ten most active users per day, and a user who exercises their right to
            erasure is tombstoned there as “Former player”.
          </p>
        </div>
      )}
    </div>
  );
}
